import { useState, type ReactNode } from 'react';
import { Icon } from '@gftg/ui';
import { useNavigate, useParams } from 'react-router';
import { CardRow } from '../components/CardRow';
import { ErrorBlock, ICON_BUTTON, LoadingBlock } from '../components/states';
import { TrackPill } from '../components/TrackPill';
import { downloadIcs, icsFilename, sessionIcs } from '../lib/calendar';
import { formatMonthDay, formatShortDay, formatTimeRange } from '../lib/datetime';
import { useEvent, useSessionDetail } from '../lib/queries';
import { useSavedSessions } from '../lib/savedSessions';

export function SessionPage() {
  const { idOrSlug = '' } = useParams();
  const navigate = useNavigate();
  const event = useEvent();
  const session = useSessionDetail(idOrSlug);
  const saved = useSavedSessions();
  const [toast, setToast] = useState<string | null>(null);

  if (event.isPending || session.isPending) {
    return <LoadingBlock label="Loading the session…" />;
  }

  if (event.isError || !event.data) {
    return (
      <ErrorBlock
        message="The conference could not be loaded."
        onRetry={() => void event.refetch()}
      />
    );
  }

  if (session.isError || !session.data) {
    return <ErrorBlock message="This session is not part of the published program." />;
  }

  const { timezone } = event.data;
  const detail = session.data;
  const day = event.data.days.find((candidate) => candidate.key === detail.dayKey);
  const isSaved = saved.isSaved(detail.id);

  const flash = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 2400);
  };

  const share = async () => {
    const url = window.location.href;
    try {
      // The phone's own share sheet where it exists; the clipboard is the fallback that always
      // does something, because a share button that silently fails is worse than none.
      if (navigator.share) {
        await navigator.share({ title: detail.title, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      flash('Link copied');
    } catch (error) {
      // Dismissing the share sheet rejects with AbortError. Deciding not to share is not a failure.
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
      flash('Could not share this link');
    }
  };

  return (
    <>
      <header className="px-gutter border-b border-border-2 bg-surface pt-3 pb-5">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => void navigate(-1)}
            aria-label="Back"
            className={ICON_BUTTON}
          >
            <Icon name="chevron-left" size={18} />
          </button>
          <p className="text-eyebrow tracking-eyebrow flex-1 font-bold text-ink-3 uppercase">
            Session
          </p>
          <button
            type="button"
            onClick={() => void share()}
            aria-label="Share this session"
            className={ICON_BUTTON}
          >
            <Icon name="share" size={17} />
          </button>
        </div>

        {detail.track && (
          <p className="mt-4">
            <TrackPill track={detail.track} className="inline-flex" />
          </p>
        )}

        <h1
          className={`font-display text-session-title mt-3 font-semibold ${
            detail.isCancelled ? 'text-ink-3 line-through' : 'text-ink'
          }`}
        >
          {detail.title}
        </h1>
      </header>

      {/* Held to a reading width on a laptop, like the presenter screen: this is a description to
          read, and the two buttons under it are aimed at rather than swiped to. */}
      <main className="px-gutter space-y-4 py-4 lg:max-w-2xl">
        <div className="rounded-card grid grid-cols-2 gap-4 bg-sand p-4">
          <Fact label="When">
            <p className="text-row font-semibold text-ink">
              {day
                ? `${formatShortDay(day.date, timezone)} ${formatMonthDay(day.date, timezone)}`
                : '—'}
            </p>
            <p className="text-subtitle text-ink-3 tabular-nums">
              {formatTimeRange(detail.startsAt, detail.endsAt, timezone)}
            </p>
          </Fact>

          <Fact label="Where">
            <p className="text-row font-semibold text-ink">
              {detail.roomName ?? 'To be announced'}
            </p>
            {detail.roomLevel && <p className="text-subtitle text-ink-3">{detail.roomLevel}</p>}
          </Fact>
        </div>

        {detail.isCancelled && (
          <Notice tone="alert" lead="Cancelled.">
            {detail.cancellationReason ?? 'The organizers have cancelled this session.'}
          </Notice>
        )}

        {detail.roomChanged && (
          <Notice
            tone="warn"
            lead={detail.roomName ? `Room updated → ${detail.roomName}.` : 'Room updated.'}
          >
            {detail.roomChangeNote ?? 'Check the room above before you set off.'}
          </Notice>
        )}

        {detail.eligibilityNote && (
          <Notice tone="neutral" lead="Who can attend.">
            {detail.eligibilityNote}
          </Notice>
        )}

        {detail.description ? (
          <p className="text-body whitespace-pre-line text-ink-2">{detail.description}</p>
        ) : (
          <p className="text-body text-ink-3">
            No description has been published for this session yet.
          </p>
        )}

        {detail.speakers.length > 0 && (
          <section>
            <h2 className="text-eyebrow tracking-eyebrow mb-2 font-bold text-ink-3 uppercase">
              Speakers
            </h2>
            <ul className="space-y-card">
              {detail.speakers.map((speaker) => (
                <li key={speaker.id}>
                  <CardRow
                    to={`/speakers/${speaker.id}`}
                    title={speaker.name}
                    subtitle={[speaker.titleRole, speaker.organization].filter(Boolean).join(' · ')}
                    personName={speaker.name}
                    photoAssetId={speaker.headshotAssetId}
                  />
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="space-y-card pt-1 lg:flex lg:gap-3 lg:space-y-0">
          {/* SCH-01: still saved on this device rather than to `starred_sessions`, so the label
              promises no email — there is a person to attribute a star to now, but no endpoint. */}
          <button
            type="button"
            onClick={() => {
              saved.toggle(detail.id);
              flash(isSaved ? 'Removed from your schedule' : 'Saved to your schedule');
            }}
            aria-pressed={isSaved}
            className={`text-button rounded-button flex min-h-touch w-full items-center justify-center gap-2 px-4 font-semibold lg:w-auto lg:px-6 ${
              isSaved ? 'bg-brand-wash text-brand-text' : 'bg-ink text-white'
            }`}
          >
            <Icon name="star" size={19} filled strokeWidth={0} />
            {isSaved ? 'Saved to my schedule' : 'Add to my schedule'}
          </button>

          <button
            type="button"
            onClick={() =>
              downloadIcs(
                icsFilename(detail.title),
                sessionIcs(detail, { name: event.data.name, venueName: event.data.venueName }),
              )
            }
            className="text-button rounded-button flex min-h-touch w-full items-center justify-center border border-border-input bg-surface px-4 font-semibold text-ink lg:w-auto lg:px-6"
          >
            Add to calendar (.ics)
          </button>
        </div>
      </main>

      {toast && (
        <p
          role="status"
          // Clear of the tab bar on a phone; on a laptop there is no bar to clear, so it sits at
          // the bottom edge of the window instead of floating 52px above it.
          className="text-subtitle shadow-float fixed bottom-[calc(var(--spacing-tabbar)+0.75rem+env(safe-area-inset-bottom))] left-1/2 z-30 -translate-x-1/2 rounded-full bg-ink px-4 py-2 text-white lg:bottom-6"
        >
          {toast}
        </p>
      )}
    </>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="text-eyebrow tracking-eyebrow font-bold text-ink-3 uppercase">{label}</p>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

/** Amber is a schedule problem, red is a cancellation. Neither is decorative (brand kit). */
const TONES = {
  alert: { box: 'border-alert-border bg-alert-bg text-alert-text', dot: 'bg-alert' },
  warn: { box: 'border-warn-border bg-warn-bg text-warn-text', dot: 'bg-warn' },
  neutral: { box: 'border-border bg-surface text-ink-2', dot: 'bg-brand' },
};

function Notice({
  tone,
  lead,
  children,
}: {
  tone: keyof typeof TONES;
  lead: string;
  children: ReactNode;
}) {
  const style = TONES[tone];

  return (
    <div className={`rounded-button text-subtitle flex gap-2.5 border p-4 ${style.box}`}>
      <span className={`mt-1.5 size-1.5 shrink-0 rounded-full ${style.dot}`} aria-hidden="true" />
      <p>
        <span className="font-semibold">{lead}</span> {children}
      </p>
    </div>
  );
}
